<script lang="ts" setup>
import { UITextMdMedium } from '@gohighlevel/ghl-ui'
import { computed, PropType, ref, watch } from 'vue'
import { IElement, ELEMENTS_STYLES } from '@gohighlevel/ghl-proposals-common'
import { useElementOption } from '@/composable/elementOptions'
import { debounce } from 'lodash'
import { useDocument } from '@/composition'
import { useBuilderStore } from '@/store/builder'
import { useAppStore } from '@/store/app'

const { updateStyles } = useElementOption()
const imageElement = ref()
const store = useBuilderStore()
const { loadingCustomValues } = useDocument()
const appStore = useAppStore()
const imageLoadError = ref(false)

const props = defineProps({
  element: {
    type: Object as PropType<IElement>,
  },
})

const refreshCount = ref(0)
const isDimensionValidationPending = ref(false)

const flatCustomValues = computed(() => ({
  ...store.flatCustomValues,
  ...appStore.flatDocumentVariables,
}))

watch(
  () => [flatCustomValues.value, loadingCustomValues.value],
  () => {
    refreshCount.value = 1 + refreshCount.value
  }
)

const customClassesForImg = computed(() => {
  const styles = props.element?.responsiveStyles.large
  return [styles?.align, styles?.imageEffect]
})

const processImageSrc = (src: string | undefined): string => {
  if (!src || typeof src !== 'string') {
    return src || ''
  }

  if (!src.includes('{{')) {
    return src
  }

  const customValues = flatCustomValues.value as Record<string, any>
  const placeholderPattern = /{{\s*([^{}]+?)\s*}}/g

  return src.replace(placeholderPattern, (match, key) => {
    const trimmedKey = key.trim()
    const value = customValues[trimmedKey]

    if (value === undefined || value === null) {
      return match
    }

    return String(value)
  })
}

const imageSrc = computed(() => {
  const src = props.element?.component?.options?.src
  return processImageSrc(src)
})

const customStyleForImageParent = computed(() => {
  const styles = props.element?.responsiveStyles.large
  return {
    backgroundColor: styles?.backgroundColor,
    marginTop: styles?.marginTop,
    marginBottom: styles?.marginBottom,
    marginLeft: styles?.marginLeft,
    marginRight: styles?.marginRight,
    paddingTop: styles?.paddingTop,
    paddingBottom: styles?.paddingBottom,
    paddingLeft: styles?.paddingLeft,
    paddingRight: styles?.paddingRight,
  }
})

const isPositiveNumber = (value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return false
  }

  const parsedValue = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsedValue) && parsedValue > 0
}

const hasValidImageDimensions = computed(() => {
  const styles = props.element?.responsiveStyles.large
  return isPositiveNumber(styles?.height) && isPositiveNumber(styles?.width)
})

const imageErrorMessage = computed<string | null>(() => {
  if (imageLoadError.value) {
    return 'common.invalidImageUrl'
  }

  if (!hasValidImageDimensions.value && !isDimensionValidationPending.value) {
    return 'validations.invalid.invalidImageSizeConfig'
  }

  return null
})

const updateStyleForImageElement = (key: string, value: string) => {
  updateStyles(key, value, props?.element?.id, props?.element?.type)
}

const updateHeightAndWidth = (dimensions?: {
  height?: number
  width?: number
}) => {
  const element = imageElement.value as HTMLImageElement | undefined
  const boundingRect = element?.getBoundingClientRect()
  const resolvedHeight = dimensions?.height ?? boundingRect?.height
  const resolvedWidth = dimensions?.width ?? boundingRect?.width

  if (isPositiveNumber(resolvedHeight)) {
    updateStyleForImageElement(
      ELEMENTS_STYLES.HEIGHT,
      Number(resolvedHeight).toFixed(2)
    )
  }

  if (isPositiveNumber(resolvedWidth)) {
    updateStyleForImageElement(
      ELEMENTS_STYLES.WIDTH,
      Number(resolvedWidth).toFixed(2)
    )
  }
}

const debouncedUpdateHeightAndWidth = debounce(
  (dimensions?: { height?: number; width?: number }) => {
    updateHeightAndWidth(dimensions)
    isDimensionValidationPending.value = false
  },
  500
)
/*
 * height and width should not be empty.
 * if its empty the calculation of offsite in PDF generation will be wrong and because of that,
 * the content will be eliminated from the PDF - So we should have the height & width
 */
watch(
  () => props.element?.component?.options?.src,
  newValue => {
    if (newValue) {
      imageLoadError.value = false // Reset error state when src changes
      isDimensionValidationPending.value = true
      updateStyleForImageElement(ELEMENTS_STYLES.HEIGHT, '')
      updateStyleForImageElement(ELEMENTS_STYLES.WIDTH, '')
    } else {
      isDimensionValidationPending.value = false
    }
  }
)

const onImgLoad = (event: Event) => {
  imageLoadError.value = false
  const target = event?.target as HTMLImageElement | null

  if (!hasValidImageDimensions.value) {
    const boundingRect = target?.getBoundingClientRect()
    debouncedUpdateHeightAndWidth({
      height: boundingRect?.height,
      width: boundingRect?.width,
    })
  }
}

const onImgError = () => {
  imageLoadError.value = true
  isDimensionValidationPending.value = false
}
</script>

<template>
  <div
    class="el-wrapper img-el"
    type="content"
    kind="image"
    ref="imageElementWrapper"
  >
    <div
      v-if="element?.component?.options?.src"
      :style="customStyleForImageParent"
    >
      <div
        v-if="imageErrorMessage"
        class="h-[100px] bg-gray-100 text-center text-lg relative overflow-auto m-[2px] flex items-center justify-center"
      >
        <UITextMdMedium class="break-words px-2">
          {{ $t(imageErrorMessage) }}
        </UITextMdMedium>
      </div>
      <img
        v-show="!imageErrorMessage"
        draggable="false"
        ref="imageElement"
        :alt="element?.component?.options?.altText"
        :class="customClassesForImg"
        :height="element?.responsiveStyles.large.height"
        :src="imageSrc"
        :width="element?.responsiveStyles.large.width"
        class="img"
        @load="onImgLoad"
        @error="onImgError"
        aria-hidden="true"
        :key="refreshCount"
      />
    </div>
    <div
      v-else
      class="h-[100px] bg-gray-100 text-center text-lg relative overflow-auto m-[2px]"
    >
      <UITextMdMedium
        class="break-words -translate-y-2/4 absolute left-0 top-[50%] w-full"
        >{{ $t('common.pleaseSelectImage') }}</UITextMdMedium
      >
    </div>
  </div>
</template>

<style lang="scss">
.el-wrapper {
  .img {
    max-width: 100%;
    max-height: 100%;
    display: block;
  }
}
</style>
